export const parseAmount = (amount, fallback = 0) => {
    if (typeof amount === 'number') {
        return Number.isFinite(amount) ? amount : fallback;
    }

    if (amount === null || amount === undefined || amount === '') return fallback;

    let value = String(amount)
        .trim()
        .replace(/\u00a0/g, '')
        .replace(/\s/g, '')
        .replace(/(?:MAD|DH)/gi, '')
        .replace(/[^\d,.(\)-]/g, '');

    const negative = /^\(.*\)$/.test(value) || value.startsWith('-');
    value = value.replace(/[()-]/g, '');

    const lastComma = value.lastIndexOf(',');
    const lastDot = value.lastIndexOf('.');
    if (lastComma >= 0 && lastDot >= 0) {
        const decimalSeparator = lastComma > lastDot ? ',' : '.';
        const groupingSeparator = decimalSeparator === ',' ? /\./g : /,/g;
        value = value.replace(groupingSeparator, '').replace(decimalSeparator, '.');
    } else if (lastComma >= 0) {
        value = value.replace(/\./g, '').replace(',', '.');
    } else if (/^\d{1,3}(?:\.\d{3})+$/.test(value)) {
        value = value.replace(/\./g, '');
    }

    const parsed = Number(value);
    if (!Number.isFinite(parsed)) return fallback;
    return negative ? -parsed : parsed;
};

// Keep the original precision. This legacy name remains for existing callers.
export const roundToNearestInteger = (amount) => amount;

const DOCUMENT_MONEY_FIELDS = new Set([
    'montantHT', 'montantTVA', 'montantTTC','montantTotalHT', 'montantTotalTaxe', 'montantTotalTTC', 'montantPaye', 'montantRegle',
    'montantDocument', 'montantAlloue', 'montantRestant', 'montantAffecte', 'montantUtilise',
    'totalAmountHt', 'totalAmountTva', 'totalAmountTtc', 'totalAmountPaid', 'invoicedAmount'
]);

export const roundDocumentAmounts = (document) => {
    if (!document || typeof document !== 'object') return document;
    if (Array.isArray(document)) return document.map(roundDocumentAmounts);

    return Object.entries(document).reduce((rounded, [key, value]) => {
        if (DOCUMENT_MONEY_FIELDS.has(key)) {
            rounded[key] = roundToNearestInteger(value);
        } else if (['lignes', 'lines', 'receiptMappings', 'allocations', 'paymentAllocations', 'utilisationAvances'].includes(key)) {
            rounded[key] = roundDocumentAmounts(value);
        } else {
            rounded[key] = value;
        }
        return rounded;
    }, {});
};

export const formatPrice = (amount, flag = true) => {
    const formattedPrice = new Intl.NumberFormat('fr-MA', {
        style: 'decimal',
        minimumFractionDigits: 0,
        maximumFractionDigits: 6
    }).format(roundToNearestInteger(amount));
    return flag ? `${formattedPrice} DH` : formattedPrice;
};

export const preventScroll = (e) => {
    e.preventDefault();
};

export const convertNumberToWords = (num) => {
    const ones = ["", "un", "deux", "trois", "quatre", "cinq", "six", "sept", "huit", "neuf", "dix", "onze", "douze", "treize", "quatorze", "quinze", "seize", "dix-sept", "dix-huit", "dix-neuf"];
    const tens = ["", "", "vingt", "trente", "quarante", "cinquante", "soixante", "soixante-dix", "quatre-vingt", "quatre-vingt-dix"];

    if (num === 0) return "zéro";

    const convertBelowHundred = (n) => {
        if (n < 20) return ones[n];
        const ten = Math.floor(n / 10);
        const one = n % 10;
        if (ten === 7 || ten === 9) {
            return tens[ten] + (one > 0 ? "-" + ones[10 + one] : "");
        }
        return tens[ten] + (one > 0 ? "-" + ones[one] : "");
    };

    const convertBelowThousand = (n) => {
        const hundred = Math.floor(n / 100);
        const rest = n % 100;
        const hundredText = hundred > 1 ? ones[hundred] + " cent" : hundred === 1 ? "cent" : "";
        const restText = convertBelowHundred(rest);
        return hundredText + (rest > 0 ? " " + restText : "");
    };


    let result = "";
    const thousand = Math.floor(num / 1000);
    const rest = num % 1000;

    if (thousand > 0) {
        result += thousand > 1 ? convertBelowThousand(thousand) + " mille" : "mille";
    }

    if (rest > 0) {
        result += (result ? " " : "") + convertBelowThousand(rest);
    }

    return result.trim();
};

export const convertAmountToWords = (amount) => {
    const value = Math.abs(parseAmount(amount));
    const dirhams = Math.floor(value);
    const centimes = Math.round((value - dirhams) * 100);
    const normalizedDirhams = centimes === 100 ? dirhams + 1 : dirhams;
    const normalizedCentimes = centimes === 100 ? 0 : centimes;
    const sign = parseAmount(amount) < 0 ? "moins " : "";
    const dirhamWords = `${convertNumberToWords(normalizedDirhams)} dirham${normalizedDirhams === 1 ? "" : "s"}`;
    const centimeWords = normalizedCentimes
        ? ` et ${convertNumberToWords(normalizedCentimes)} centime${normalizedCentimes === 1 ? "" : "s"}`
        : "";
    return `${sign}${dirhamWords}${centimeWords}`;
};

export const getPrixAchat = (article, fournisseurId) => {
    if (article.fournisseurs && article.fournisseurs.length > 0) {
        const fournisseurRelation = article.fournisseurs.find(f => f.fournisseurId === fournisseurId);
        if (fournisseurRelation) {
            return fournisseurRelation.prixAchatHT;
        }
    }
    return 0; // Default purchase price
};

export const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleDateString();
};

export const formatSigned = (debit, credit) => {
  const d = Number(debit || 0);
  const c = Number(credit || 0);
  const solde = Math.abs(d - c); // always positive value

  if (d > c) {
    // negative solde
    return `-${formatPrice(solde, false)}`;
  } else if (c > d) {
    // positive solde
    return `+${formatPrice(solde, false)}`;
  } else {
    // zero
    return "0.00";
  }
};

export const calcDetail = (row) => {
  const largeur = Number(row.largeur || 0);
  const longeur = Number(row.longeur || 0);

  const largeurML = largeur + 0.2;

  let nbP = longeur / 0.6;
  nbP = (nbP % 1 >= 0.83) ? Math.ceil(nbP) : Math.floor(nbP);
  if (row.depart) nbP += 1;

  const surface = largeur * longeur;
  const nbHourdis = (surface * 8.4) + 5;

  return {
    ...row,
    largeurML,
    nombrePoutrelle: nbP,
    surface,
    nombreHourdis: nbHourdis,
    montantLigne: nbP * (row.prixUnitaire || 0),
  };
};

export const calcTotals = (details) => {
  const totals = details.reduce((a, d) => {
    a.surface += d.surface || 0;
    a.ml += d.largeurML || 0;
    a.hourdis += d.nombreHourdis || 0;
    a.poutrelle += d.nombrePoutrelle || 0;
    a.total += d.montantLigne || 0;
    return a;
  }, { surface:0, ml:0, hourdis:0, poutrelle:0, total:0 });

  totals.nombrePTS = Math.ceil(totals.surface / 10);
  totals.prixMetreCarre =
    totals.surface ? totals.total / totals.surface : 0;

  return totals;
};
